import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { ViewScopeService } from '../../../services/view-scope.service';
import { AuthStateService } from '../../../services/auth-state.service';

interface UADataBrand {
	brand: string;
	version: string;
}

interface NavigatorUAData {
	brands: UADataBrand[];
	mobile: boolean;
	platform: string;
	getHighEntropyValues(hints: string[]): Promise<any>;
}

@Component({
	selector: 'app-diagnostic',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './diagnostic.component.html',
	styleUrls: ['./diagnostic.component.scss']
})
export class DiagnosticComponent implements OnInit, OnDestroy {
	@Input() userName: string | null = 'Anonymous User';
	@Input() environment: 'demo' | 'development' | 'production' = 'development';

	customerId: number | null = null;
	customerName: string | null = null;
	companyId: number | null = null;
	companyName: string | null = null;
	browser = '';
	osName = '';
	viewport = '';
	screenResolution = '';
	devicePixelRatio = 1;
	colorScheme = 'light';
	language = '';
	timezone: string | undefined;
	cpuCores: number | undefined;
	deviceMemory: number | string = 'unknown';
	online = true;
	connection = 'unknown';
	downlinkMbps: number | null = null;
	rtt: number | null = null;
	publicIp: string | null = null;
	isLoadingPublicIp = false;
	lastUpdatedAt: string | null = null;

	constructor(
		public config: DynamicDialogConfig,
		public ref: DynamicDialogRef,
		private viewScopeService: ViewScopeService,
		private authStateService: AuthStateService
	) {
		try {
			const data: any = (config && (config as any).data) || {};
			if (data && typeof data.userName !== 'undefined' && data.userName !== null) {
				this.userName = String(data.userName);
			} else {
				// Fallback: combine the name AuthStateService holds in memory
				const first = this.authStateService.getFirstName().trim();
				const last = this.authStateService.getLastName().trim();
				const combined = [first, last].filter(Boolean).join(' ');
				if (combined) this.userName = combined;
			}

			if (typeof data.environment !== 'undefined') this.environment = data.environment;
		} catch (e) {
			// Not opened via DynamicDialog or no data provided
		}
	}

	private onlineFallback = () => (this.online = true);
	private offlineFallback = () => (this.online = false);

	ngOnInit(): void {
		this.collectDiagnostics();
		this.fetchPublicIp().catch(() => { });

		window.addEventListener('online', this.onlineFallback);
		window.addEventListener('offline', this.offlineFallback);
	}

	ngOnDestroy(): void {
		window.removeEventListener('online', this.onlineFallback);
		window.removeEventListener('offline', this.offlineFallback);
	}

	collectDiagnostics(): void {
		try {
			this.customerId = this.viewScopeService.getCurrentCustomer() ?? null;
			this.customerName = this.viewScopeService.getCurrentCustomerName() ?? null;
			this.collectCompanyContext();

			const userAgent = navigator.userAgent || '';
			const uaData = (navigator as any).userAgentData as NavigatorUAData | undefined;

			if (uaData && Array.isArray(uaData.brands)) {
				const brand = uaData.brands.find(b => b.brand !== 'Not A;Brand') || uaData.brands[0];
				this.browser = [brand?.brand, brand?.version].filter(Boolean).join(' ');
				this.osName = uaData.platform || this.parseOSFromUserAgent(userAgent);
			} else {
				this.browser = this.parseBrowserFromUserAgent(userAgent);
				this.osName = this.parseOSFromUserAgent(userAgent);
			}

			const sw = window.screen || {};
			this.screenResolution = `${sw.width || 0}x${sw.height || 0}`;
			this.viewport = `${window.innerWidth}x${window.innerHeight}`;
			this.devicePixelRatio = window.devicePixelRatio || 1;

			const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
			if (mq) this.colorScheme = mq.matches ? 'dark' : 'light';

			this.language = navigator.language || '';
			try {
				this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			} catch {
				this.timezone = undefined;
			}

			this.cpuCores = navigator.hardwareConcurrency || undefined;
			this.deviceMemory = (navigator as any).deviceMemory || 'unknown';
			this.online = navigator.onLine === true;

			const conn = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
			if (conn) {
				this.connection = conn.effectiveType || conn.type || 'unknown';
				this.downlinkMbps = conn.downlink || null;
				this.rtt = conn.rtt || null;
			}

			this.lastUpdatedAt = new Date().toLocaleTimeString();
		} catch (err) {
			console.error('Failed to collect diagnostics', err);
		}
	}

	private collectCompanyContext(): void {
		try {
			const selectedCompany = localStorage.getItem('selectedCompany');
			if (!selectedCompany) {
				this.companyId = null;
				this.companyName = null;
				return;
			}

			const parsedCompany = JSON.parse(selectedCompany);
			this.companyId = typeof parsedCompany?.id === 'number' ? parsedCompany.id : null;
			this.companyName = typeof parsedCompany?.name === 'string' ? parsedCompany.name : null;
		} catch {
			this.companyId = null;
			this.companyName = null;
		}
	}

	parseBrowserFromUserAgent(userAgent: string): string {
		const edge = /Edg\/(\d+)/i.exec(userAgent);
		if (edge) return `Edge ${edge[1]}`;

		const chrome = /Chrome\/(\d+)/i.exec(userAgent);
		if (chrome) return `Chrome ${chrome[1]}`;

		const firefox = /Firefox\/(\d+)/i.exec(userAgent);
		if (firefox) return `Firefox ${firefox[1]}`;

		const safari = /Version\/(\d+).*Safari/i.exec(userAgent);
		if (safari) return `Safari ${safari[1]}`;

		return 'Unknown browser';
	}

	parseOSFromUserAgent(ua: string): string {
		if (/Windows NT 10\.0/.test(ua)) return 'Windows 10/11';
		if (/Windows NT 6\./.test(ua)) return 'Windows';
		if (/Android/.test(ua)) return 'Android';
		if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
		if (/Mac OS X/.test(ua)) return 'macOS';
		if (/Linux/.test(ua)) return 'Linux';
		return 'Unknown OS';
	}

	async fetchPublicIp(): Promise<void> {
		if (this.isLoadingPublicIp) return;
		this.isLoadingPublicIp = true;

		try {
			const resp = await fetch('https://api.ipify.org?format=json');
			if (resp.ok) {
				const data = await resp.json();
				this.publicIp = data.ip || null;
			}
		} catch {
			this.publicIp = null;
		} finally {
			this.isLoadingPublicIp = false;
		}
	}

	async refresh() {
		this.collectDiagnostics();
		await this.fetchPublicIp();
	}
}